import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { EvaluationBlock } from '@interactive-room';
import { validateExamBlock } from '../components/ExamBlockInlineEditor';

export interface PrintInlineEditContextValue {
  isEditing: boolean;
  block: EvaluationBlock;
  /** Persiste el bloque (valida payload). */
  commitBlock: (next: EvaluationBlock) => void;
  /** Parchea campos de primer nivel del payload y guarda. */
  patchPayload: (patch: Record<string, unknown>) => void;
  setLabel: (label: string) => void;
}

const PrintInlineEditContext = createContext<PrintInlineEditContextValue | null>(null);

export function PrintInlineEditProvider({
  isEditing,
  block,
  onChange,
  children,
}: {
  isEditing: boolean;
  block: EvaluationBlock;
  onChange?: (block: EvaluationBlock) => void;
  children: ReactNode;
}) {
  const value = useMemo<PrintInlineEditContextValue>(
    () => ({
      isEditing,
      block,
      commitBlock: (next) => {
        if (!onChange) return;
        onChange(validateExamBlock(next));
      },
      patchPayload: (patch) => {
        if (!onChange) return;
        const payload = {
          ...((block.payload ?? {}) as Record<string, unknown>),
          ...patch,
        };
        onChange(
          validateExamBlock({
            ...block,
            payload: payload as EvaluationBlock['payload'],
          }),
        );
      },
      setLabel: (label) => {
        if (!onChange) return;
        const payload = {
          ...((block.payload ?? {}) as Record<string, unknown>),
          title: label,
        };
        onChange(
          validateExamBlock({
            ...block,
            label,
            payload: payload as EvaluationBlock['payload'],
          }),
        );
      },
    }),
    [block, isEditing, onChange],
  );

  return <PrintInlineEditContext.Provider value={value}>{children}</PrintInlineEditContext.Provider>;
}

export function usePrintInlineEdit(): PrintInlineEditContextValue | null {
  return useContext(PrintInlineEditContext);
}

export function usePrintInlineEditing(): boolean {
  return Boolean(useContext(PrintInlineEditContext)?.isEditing);
}
